import React, {useState} from "react";
import {ICartItemPrice, IWishlistItem} from "@connox/typescript-library";
import {useTranslation, Trans} from "react-i18next";
import {getShopId} from "../../lib/getShopId";
import {AddToCartButton, AddToCartButtonFallback} from "../../cart/AddToCartButton/AddToCartButtonPortal";
import {useWishlistContext} from "../WishlistContextProvider/WishlistContextProvider";
import {isCart, useUnsafeCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import styles from "./WishlistItem.module.scss";
import {isoLocales} from "../../lib/payments/PaymentController";
import EnergyLabel from "../../energyLabel/EnergyLabel";
import LegalAttributes from "../LegalAttributes";
import Image from "../../ProductSeries/Image";

const WishlistItem: React.FC<{item: IWishlistItem}> = ({item}) => {
  const {images, title, itemId, isActive, prices, url, itemClassId, attributes} = item.item;
  const {deleteItem} = useWishlistContext();
  const context = useUnsafeCartContext();
  const [isLoading, setIsLoading] = useState(false);
  const {t} = useTranslation(["customerAccount", "cart"]);

  // is there an energy efficiency class present
  const energyEfficiency = attributes.find((attrib) => attrib.itemAttributeId === 100361);
  const energyLabel = images.find((image) => !!image.energyLabel)?.energyLabel || undefined;
  const wishingDateString = t("Gewünscht am: {{wishingDate}}", {
    wishingDate: new Intl.DateTimeFormat(isoLocales[getShopId()].replace("_", "-")).format(new Date(item.modified)),
  });

  const onRemoveClick = () => {
    setIsLoading(true);
    void deleteItem(itemId);
  };

  return (
    <div className={`${styles.wrapper} ${isActive ? "" : styles.inactive}`}>
      <div className={styles.itemThumb}>
        <a href={url} title={title}>
          <Image item={item.item} />
        </a>
      </div>
      <div className={styles.details}>
        <a href={url} className={styles.title}>
          {title}
        </a>

        <div>
          {!!energyEfficiency && (
            <EnergyLabel energyClass={energyEfficiency.value ?? "G"} media={energyLabel} size="small" />
          )}
          <ItemPrice prices={prices[0]} />
          {/* itemClassId 6 means it is a gift voucher */}
          {itemClassId !== 6 && (
            <div className={styles.shippingInfo}>
              {isoLocales[getShopId()] !== "en_GB" && (
                <span>
                  {t("inkl. MwSt.")}
                  {", "}
                </span>
              )}
              <Trans i18nKey={"zzglVersand"} ns={"customerAccount"}>
                zzgl.
                <a href={t("/versand.html")} target="_blank" rel="noreferrer">
                  Versand
                </a>
              </Trans>
            </div>
          )}
        </div>

        <div className={styles.date}>{wishingDateString}</div>

        <LegalAttributes attributes={attributes} />

        {!isActive && (
          <div className={styles.notAvailableNote}>{t("Dieser Artikel ist leider nicht mehr verfügbar.")}</div>
        )}

        {context && (
          <div className={styles.buttonsWrapper}>
            {isActive &&
              (isCart(context) ? (
                <AddToCartButton itemId={itemId} showQuantity={false} />
              ) : (
                <AddToCartButtonFallback itemId={itemId} context={context} />
              ))}

            <div className={`${styles.removeButton}`} onClick={onRemoveClick}>
              <span className={`${styles.buttonIcon} ${isLoading ? styles.loading : ""}`} />
              <span className={styles.buttonLabel}>{t("Aus Liste entfernen")}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ItemPrice: React.FC<{prices: ICartItemPrice}> = ({prices}) => {
  const {price, retail} = prices;
  const {t} = useTranslation("cart");
  const whitespaceWrapping = (str: string) => str.replace(/\s/g, "\u00A0");

  if (price.amount !== retail.amount) {
    return (
      <div className={styles.priceRetail}>
        <span className={styles.oldPrice}>{t("UVP {{price}}", {price: whitespaceWrapping(retail.formatted)})}</span>{" "}
        <strong>{whitespaceWrapping(price.formatted)}</strong>
        {isoLocales[getShopId()] === "en_GB" && <span className={styles.star}>*</span>}
      </div>
    );
  }

  return (
    <strong className={styles.itemPriceWrapper}>
      {price.amount === "0.00" ? (
        <span> {t("gratis")}</span>
      ) : (
        <div>
          <span>{whitespaceWrapping(price.formatted)}</span>
          {isoLocales[getShopId()] === "en_GB" && <span className={styles.star}>*</span>}
        </div>
      )}
    </strong>
  );
};

export default WishlistItem;
